import { FormEntry } from "../../../types/types";
import { getRegistrantsContainer } from "../../../utils/utils";
import { formMappings } from "../../../config/formMappings";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

// Helper function to get field value, trying both v1 and v2 field names
function getFieldValue(registrant: any, v1Field: string, v2Field: string): any {
  return registrant[v2Field] ?? registrant[v1Field] ?? "";
}

export default function PaymentInfoCard(props: { entry: FormEntry }) {
  // Get form mapping from Redux state
  const activeFormName = useSelector(
    (state: RootState) => state.activeForm.activeFormName
  );
  const formMapping =
    formMappings[activeFormName as keyof typeof formMappings];

  // Get family discount percentage from formMappings (default to 0.12 if not found)
  const familyDiscountPercentage = formMapping?.familyDiscount ?? 0.12;

  // Get registrants container for both form versions
  const registrantsContainer = getRegistrantsContainer(props.entry.meta);
  const registrants = registrantsContainer || {};
  const registrantKeys = Object.keys(registrants).filter((key) =>
    key.startsWith("i")
  );

  // Collect registration types and fees from registrants
  const registrationTypes: string[] = [];
  const registrationFees = registrantKeys.reduce((sum, key) => {
    const registrantMeta = registrants[key];
    if (registrantMeta) {
      // Get registration type (try both v1 and v2 field names)
      const registrationType = getFieldValue(
        registrantMeta,
        "5yusq3",
        "5yusq32"
      );
      if (registrationType) {
        registrationTypes.push(registrationType);
      }

      // Get registration fee value (try both v1 and v2 field names)
      const feeValue = getFieldValue(
        registrantMeta,
        "5yusq3-value",
        "5yusq32-value"
      );
      if (feeValue) {
        const registrantFee = parseFloat(feeValue);
        if (!isNaN(registrantFee)) {
          sum += registrantFee;
        }
      }
    }
    return sum;
  }, 0);

  // Get number of registrants (try both v1 and v2 field names, or use human-readable name)
  const numberOfRegistrants =
    Number(props.entry.meta["Number of Registrants"]) ||
    Number(props.entry.meta["yc9mo2"]) ||
    Number(props.entry.meta["yc9mo"]) ||
    registrantKeys.length ||
    0;

  // Calculate family discount based on registration fees and round to two decimal places
  const familyDiscount =
    numberOfRegistrants >= 4
      ? Number((registrationFees * familyDiscountPercentage).toFixed(2))
      : 0;

  // Ensure donation is a number, or default to 0 (try both versions)
  const donation =
    Number(props.entry.meta["avjwx3-value"]) ||
    Number(props.entry.meta["avjwx2-value"]) ||
    0;

  // Parse the number of tables needed as a number
  const numberOfTablesNeeded =
    Number(props.entry.meta["How many tables do you need? ($25/per table)"]) ||
    0;

  // Calculate bazaar table fees and round to two decimal places
  const bazaarTableFees = Number((numberOfTablesNeeded * 25).toFixed(2));

  // Calculate the total and round to two decimal places
  const total = Number(
    (registrationFees - familyDiscount + donation + bazaarTableFees).toFixed(2)
  );

  // Get total amount (try both v1 and v2 field names)
  const totalAmount =
    props.entry.meta["p2l9j2-value"] ||
    props.entry.meta["p2l9j-value"] ||
    total.toFixed(2);

  // Get paid status (try human-readable name first, then field keys)
  const paidStatus =
    props.entry.meta["Paid?"] ||
    props.entry.meta["fmz5v32"] ||
    props.entry.meta["fmz5v3"] ||
    "";

  return (
    <>
      <h2 className="sr-only">Payment Summary</h2>
      <div className="rounded-lg bg-gray-50 shadow-sm ring-1 ring-gray-900/5">
        <dl className="flex flex-wrap">
          <div className="flex-auto pl-6 pt-6">
            <dt className="text-sm/6 font-semibold text-gray-900">Amount</dt>
            <dd className="mt-1 text-xl font-semibold text-gray-900">
              ${totalAmount}
            </dd>
          </div>
          <div className="flex-none self-end px-6 pt-4">
            <dt className="sr-only">Status</dt>
            {paidStatus === "Yes" ? (
              <dd className="rounded-md bg-green-50 px-2 py-1 text-md font-medium text-green-600 ring-1 ring-inset ring-green-600/20">
                Paid
              </dd>
            ) : (
              <dd className="rounded-md bg-red-50 px-2 py-1 text-md font-medium text-red-600 ring-1 ring-inset ring-red-600/20">
                Not Paid
              </dd>
            )}
          </div>
          <div className="mt-6 flex w-full flex-none gap-x-4 border-t border-gray-900/5 px-6 pt-6">
            <table className="w-full whitespace-nowrap text-left text-sm/6">
              <colgroup>
                <col className="w-3/4" />
                <col className="w-1/4" />
              </colgroup>
              <tbody>
                <tr>
                  <td className="max-w-0 px-0 py-1 align-top">
                    <div className="truncate font-medium text-gray-900">
                      Registration Fees
                    </div>
                    <div className="truncate text-gray-500">
                      {registrationTypes.length > 0
                        ? registrationTypes.join(", ")
                        : "N/A"}
                    </div>
                  </td>
                  <td className="py-1 pr-0 text-lg text-right align-top tabular-nums text-gray-700">
                    ${registrationFees.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td className="max-w-0 px-0 py-1 align-top">
                    <div className="truncate font-medium text-gray-900">
                      Family Discount
                    </div>
                    <div className="truncate text-gray-500">
                      Group has {numberOfRegistrants} registrants
                    </div>
                  </td>
                  <td className="py-1 pl-8 pr-0 text-lg text-right align-top tabular-nums text-red-700">
                    -${familyDiscount.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td className="max-w-0 px-0 py-1 align-top">
                    <div className="truncate font-medium text-gray-900">
                      Optional Donation
                    </div>
                  </td>
                  <td className="py-1 pl-8 pr-0 text-lg text-right align-top tabular-nums text-gray-700">
                    ${donation.toFixed(2)}
                  </td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="max-w-0 px-0 py-1 align-top">
                    <div className="truncate font-medium text-gray-900">
                      Bazaar Table Fees
                    </div>
                    <div className="truncate text-gray-500">
                      $25/table x {numberOfTablesNeeded} tables
                    </div>
                  </td>
                  <td className="py-1 pl-8 pr-0 text-lg text-right align-top tabular-nums text-gray-700">
                    ${bazaarTableFees.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td className="max-w-0 px-0 py-5 align-top">
                    <div className="truncate font-medium text-gray-900">
                      Total
                    </div>
                  </td>
                  <td className="py-5 pl-8 pr-0 text-lg font-bold text-right align-top tabular-nums text-gray-700">
                    ${total.toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </dl>
        {/* <div className="mt-6 border-t border-gray-900/5 px-6 py-6">
              <a href="#" className="text-sm/6 font-semibold text-gray-900">
                Download receipt <span aria-hidden="true">&rarr;</span>
              </a>
            </div> */}
      </div>
    </>
  );
}
